import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, IconButton, InputLabel, MenuItem,
  Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon, Add as AddIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { excerpt } from '../../common/markdown';
import { adminListTips, deleteTip } from '../../api/tips';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { AdminTipSummary } from '../../types/tip';

export function TipsAdminPage() {
  const navigate = useNavigate();
  const { logout } = useAuth0();

  const { request: listRequest } = useApiCallWithAuth(adminListTips);
  const { request: deleteRequest } = useApiCallWithAuth(deleteTip);

  const [tips, setTips] = useState<AdminTipSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminTipSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [domainFilter, setDomainFilter] = useState('All');

  function loadTips() {
    setLoading(true);
    listRequest()
      .then((data: AdminTipSummary[]) => setTips(data))
      .catch(() => setError('Failed to load tips'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadTips(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRequest(deleteTarget.tipId);
      setDeleteTarget(null);
      loadTips();
    } catch {
      setError('Failed to delete tip');
    } finally {
      setDeleting(false);
    }
  }

  const filteredTips = domainFilter === 'All'
    ? tips
    : tips.filter(t => t.domain === domainFilter);

  return (
    <Box sx={{ pt: 3 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Typography variant='h5' fontWeight={700}>Tips Admin</Typography>
        <Stack direction='row' spacing={1}>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => navigate('/tips/new')}
          >
            New Tip
          </Button>
          <Button
            variant='outlined'
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Sign Out
          </Button>
        </Stack>
      </Stack>

      <Stack direction='row' alignItems='center' sx={{ mb: 2 }}>
        <FormControl size='small' sx={{ minWidth: 160 }}>
          <InputLabel id='domain-filter-label'>Domain</InputLabel>
          <Select
            labelId='domain-filter-label'
            value={domainFilter}
            label='Domain'
            onChange={e => setDomainFilter(e.target.value)}
          >
            <MenuItem value='All'>All</MenuItem>
            <MenuItem value='General'>General</MenuItem>
            <MenuItem value='Sports'>Sports</MenuItem>
            <MenuItem value='Running'>Running</MenuItem>
            <MenuItem value='Food'>Food</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading && <CircularProgress size={24} />}
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && !error && filteredTips.length === 0 && (
        <Typography color='text.secondary'>No tips yet.</Typography>
      )}

      {!loading && filteredTips.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Domain</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Tip</strong></TableCell>
              <TableCell align='right'><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTips.map(tip => (
              <TableRow key={tip.tipId} hover>
                <TableCell>
                  <Typography variant='body2'>{tip.title}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    {new Date(`${tip.date}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{tip.domain}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={tip.isPublished ? 'Published' : 'Draft'}
                    size='small'
                    color={tip.isPublished ? 'success' : 'default'}
                    variant={tip.isPublished ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 400 }}>
                  <Typography variant='body2' noWrap>{excerpt(tip.tipContent, 120)}</Typography>
                </TableCell>
                <TableCell align='right'>
                  <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
                    {tip.isPublished && (
                      <Tooltip title='View public tip'>
                        <IconButton size='small' onClick={() => navigate('/tips')}>
                          <VisibilityIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title='Edit'>
                      <IconButton size='small' onClick={() => navigate(`/tips/${tip.tipId}/edit`)}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete'>
                      <IconButton size='small' color='error' onClick={() => setDeleteTarget(tip)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete tip?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The tip for "{deleteTarget ? new Date(`${deleteTarget.date}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}" will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color='error' variant='contained' disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
